/**
 * sceneViewConfig.ts — Pure helpers that merge project/scene view limits and mobile_view.
 * Kept free of React/Redux imports so they can be unit tested.
 */
import type { I_ConfigScene } from '@containers/Home/types/I_Scene';

export const DEFAULT_SCENE_VIEW: Required<
  Pick<I_ConfigScene, 'limit_view' | 'zoom_limit' | 'default_view'>
> = {
  limit_view: {
    top: -90,
    bottom: 90,
    left: -180,
    right: 180,
  },
  zoom_limit: {
    min: 60,
    max: 150,
  },
  // No zoom_lv here: a default zoom would make every merged scene look like it has an
  // explicit zoom and switch it to MFOV (see shouldUseMfov).
  default_view: {
    x: 0,
    y: 0,
  },
};

/** Fallback fov used when a zoom has to be clamped but no value is configured. */
export const DEFAULT_SCENE_ZOOM_LV = 90;

export const clampZoomLv = (
  zoom: number | undefined,
  min: number,
  max: number,
  fallback = DEFAULT_SCENE_ZOOM_LV,
) => {
  const value = Number(zoom);
  const raw = Number.isFinite(value) ? value : fallback;
  return Math.min(max, Math.max(min, raw));
};

/**
 * MFOV is only used when the scene has an explicit default zoom (zoom_lv).
 * Scenes without it keep krpano's default fov type so old tours render as before.
 */
export const shouldUseMfov = (config?: I_ConfigScene) =>
  config?.default_view?.zoom_lv !== undefined;

export const mergeSceneView = (
  sceneConfig?: I_ConfigScene,
  projectGlobalViewConfig?: I_ConfigScene,
): Pick<
  I_ConfigScene,
  'limit_view' | 'zoom_limit' | 'default_view' | 'mobile_view'
> => ({
  limit_view: {
    ...DEFAULT_SCENE_VIEW.limit_view,
    ...projectGlobalViewConfig?.limit_view,
    ...sceneConfig?.limit_view,
  },
  zoom_limit: {
    ...DEFAULT_SCENE_VIEW.zoom_limit,
    ...projectGlobalViewConfig?.zoom_limit,
    ...sceneConfig?.zoom_limit,
  },
  default_view: {
    ...DEFAULT_SCENE_VIEW.default_view,
    ...projectGlobalViewConfig?.default_view,
    ...sceneConfig?.default_view,
  },
  mobile_view:
    sceneConfig?.mobile_view !== undefined
      ? sceneConfig.mobile_view
      : projectGlobalViewConfig?.mobile_view,
});

export const applyMobileViewOverrides = (
  baseView: I_ConfigScene,
  isMobile: boolean,
): I_ConfigScene => {
  if (!isMobile || !baseView.mobile_view) return baseView;
  const mobile = baseView.mobile_view;
  const zoom_limit = {
    ...baseView.zoom_limit,
    ...mobile.zoom_limit,
  };
  const fovmin = zoom_limit.min ?? DEFAULT_SCENE_VIEW.zoom_limit.min;
  const fovmax = zoom_limit.max ?? DEFAULT_SCENE_VIEW.zoom_limit.max;
  const explicitZoom =
    mobile.default_view?.zoom_lv ?? baseView.default_view?.zoom_lv;
  // Only clamp a zoom that was actually configured; otherwise keep zoom_lv unset so
  // the scene stays on krpano's default fov type instead of MFOV.
  const default_view =
    explicitZoom !== undefined
      ? {
          ...baseView.default_view,
          zoom_lv: clampZoomLv(
            explicitZoom,
            fovmin,
            fovmax,
            DEFAULT_SCENE_ZOOM_LV,
          ),
        }
      : baseView.default_view;
  return {
    ...baseView,
    limit_view: { ...baseView.limit_view, ...mobile.limit_view },
    zoom_limit,
    default_view,
  };
};
